import React, { useEffect, useState } from 'react';
import { withStyles, createStyles } from '@material-ui/core/styles';
import moment from 'moment-timezone';
import { Grid, Typography, Theme } from '@material-ui/core';
import { connect } from 'react-redux';
import { Push, push } from 'connected-react-router';
import { useAppDispatch } from 'redux/hooks';
import ToastActions from 'redux/toast';
import {
  EUser,
  ERate,
  ENotice,
  EOrganization,
  ESnapshotExists,
  exists,
  MailDelivery
} from 'lib/types';
import { LineItem } from 'lib/types/invoices';
import { FormStep } from 'components/forms';
import {
  COLUMN_NAME,
  calculateInvoicePricing,
  getConvenienceFeeWithRepFeeFromRateOrNewspaper,
  getDistributeFeeSettings,
  getInvoiceLineItems,
  shouldDistributeFee
} from 'lib/pricing';
import { dbToUICurrencyString } from 'lib/pricing/ui';
import LoadingState from 'components/LoadingState';
import {
  getCurrencySymbol,
  getDueDate,
  shouldBulkInvoiceUser_v2
} from 'lib/helpers';
import FreeformCModal from 'components/modals/FreeFormCModal';
import { getCustomer, getCustomerName } from 'lib/notice/customer';
import { getFirebaseContext } from 'utils/firebase';
import { BillingService } from 'services';
import { isAffidavitDisabled } from 'lib/affidavits';
import { getRecipientName } from 'lib/utils/invoices';
import { useNoticeAffidavitSettings } from 'lib/frontend/hooks/useNoticeAffidavitSettings';
import { getMailDataFromNoticeOrDraft } from 'lib/mail';
import { isEqual } from 'lodash';
import { safeStringify } from 'lib/utils/stringify';
import { logAndCaptureCriticalError, logAndCaptureException } from 'utils';
import { ColumnService } from 'lib/services/directory';
import {
  INVOICE_CREATION_IN_PROGRESS_ERROR,
  NOTICE_HAS_INVOICE_ERROR
} from 'services/billing';
import InvoiceFormCustomerTable from './InvoiceFormCustomerTable';
import InvoiceFormPaperInfoTable from './InvoiceFormPaperInfoTable';
import InvoiceFormTable from './InvoiceFormTable';
import InvoiceFormTotalDueTable from './InvoiceFormTotalDueTable';
import InvoiceFormDueDate from './InvoiceFormDueDate';
import InvoiceConfirm from './InvoiceConfirm';
import copy from './copy';
import InvoiceFormMemo from './InvoiceFormMemo';
import NoticePreview from '../../noticePreview';
import { useCheckInvoiceCreationInitiated } from './useCheckInvoiceCreationInitiated';

const mapStateToProps = (state: any) => ({
  user: state.auth.user,
  activeOrganization: state.auth.activeOrganization,
  isPublisher: state.auth.isPublisher
});

const styles = (theme: Theme) => {
  return createStyles({
    formContainer: {
      marginTop: theme.spacing(2)
    },
    invoiceHeading: {
      marginBottom: theme.spacing(2)
    },
    totalDueTable: {
      marginTop: theme.spacing(2)
    },
    modalCloseButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500]
    },
    container: {
      width: '66%',
      maxHeight: '100vh',
      overflow: 'scroll'
    }
  });
};

type InvoiceFormProps = {
  forward: () => void;
  forwardLabel: string;
  back: () => void;
  classes: Record<string, string>;
  advertiserSnap: ESnapshotExists<EUser>;
  rateSnap: ESnapshotExists<ERate>;
  noticeSnap: ESnapshotExists<ENotice>;
  newspaperSnap: ESnapshotExists<EOrganization>;
  activeOrganization: ESnapshotExists<EOrganization>;
  isPublisher?: boolean;
  user: ESnapshotExists<EUser> | undefined;
  push: Push;
};

function InvoiceForm({
  forward,
  forwardLabel,
  back,
  classes,
  advertiserSnap,
  rateSnap,
  noticeSnap,
  newspaperSnap,
  activeOrganization,
  isPublisher,
  user,
  push
}: InvoiceFormProps) {
  const { postWithoutFormatting, pricing } = noticeSnap.data();

  const emptyLineItems: LineItem[] = [
    { amount: 0, date: new Date(), type: null }
  ];

  const initialLineItems = postWithoutFormatting
    ? emptyLineItems
    : pricing
    ? getInvoiceLineItems(pricing, newspaperSnap, rateSnap)
    : [];

  const [mail, setMail] = useState<MailDelivery[]>();
  const [lineItems, setLineItems] = useState<LineItem[]>(initialLineItems);
  const [taxPercent, setTaxpercent] = useState(
    postWithoutFormatting ? 0 : pricing?.taxPct ?? 0
  );
  const [openModal, setOpenModal] = useState(false);
  const [customMemo, setCustomMemo] = useState('');

  const [customId, setCustomId] = useState(noticeSnap.data().customId || '');
  const [invoiceOutsideColumn, setInvoiceOutsideColumn] = useState(false);
  const [organizationName, setOrganizationName] = useState<string>();
  const [advertiserName, setAdvertiserName] = useState<string>();
  const [dueDate, setDueDate] = useState<number>();
  const [requireUpfrontPayment, setRequireUpfrontPayment] = useState<boolean>();
  const [ianaTimezone, setIanaTimezone] = useState<string>();
  const [isWithinBulkPayments, setIsWithinBulkPayments] = useState<
    boolean | undefined
  >();
  const [isConfirmingInvoice, setIsConfirmingInvoice] = useState(false);
  const [preventSubmit, setPreventSubmit] = useState(false);

  const invoiceCreationInitiated = useCheckInvoiceCreationInitiated(noticeSnap);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isEqual(initialLineItems, lineItems)) {
      setLineItems(initialLineItems);
    }
  }, [safeStringify(pricing?.lineItems)]);

  const handleChange = (i: number, amountInCents: number) => {
    const newValues = [...lineItems];

    // Max price for a line item is $30,000 (likely arbitrary?)
    newValues[i].amount = Math.min(30000 * 100, Math.round(amountInCents));
    setLineItems(newValues);
  };

  useEffect(() => {
    if (exists(newspaperSnap)) {
      const { iana_timezone } = newspaperSnap.data();
      setIanaTimezone(iana_timezone);
      void (async () => {
        const dueDate = await getDueDate(
          getFirebaseContext(),
          noticeSnap,
          requireUpfrontPayment
        );
        setDueDate(dueDate);
        const dueDateMoment = moment(dueDate * 1000);
        if (moment().isAfter(dueDateMoment)) {
          setRequireUpfrontPayment(false);
        }
      })();
    }
  }, [requireUpfrontPayment, safeStringify(newspaperSnap.data())]);

  useEffect(() => {
    const fetchOrgAndAdvertiserName = async () => {
      let filerOrg;
      const { filedBy } = noticeSnap.data();
      if (filedBy) {
        filerOrg = await filedBy.get();
      }
      const ctx = getFirebaseContext();
      const customer = await getCustomer(ctx, advertiserSnap, newspaperSnap);
      setOrganizationName(filerOrg?.data()?.name || '');
      setAdvertiserName(getCustomerName(customer, advertiserSnap, false));
    };

    const fetchMail = async () => {
      const mail = await getMailDataFromNoticeOrDraft(noticeSnap.ref);
      setMail(mail);
    };

    if (noticeSnap && exists(noticeSnap)) {
      void fetchOrgAndAdvertiserName();
      void fetchMail();
    }

    if (!exists(noticeSnap) || !noticeSnap.data().pricing) {
      setPreventSubmit(true);
      logAndCaptureCriticalError(
        ColumnService.PAYMENTS,
        new Error('Notice does not have pricing data when creating invoice'),
        'Notice is missing pricing data. Fix this by going through the edit flow and saving the notice again.',
        {
          userEmail: user?.data()?.email || 'Unknown',
          noticeId: noticeSnap.id
        }
      );
    } else {
      setPreventSubmit(false);
    }
  }, [safeStringify(noticeSnap.data()), newspaperSnap?.id]);

  const handleConfirm = async () => {
    setIsConfirmingInvoice(true);

    /**
     * Greetings future code prospectors! You may notice that we are
     * updating the custom ID on the notice twice when creating an invoice:
     * here, and again upon completion of invoice creation (see
     * functions/src/invoices/helper.ts). This may seem redundant, but please
     * be aware that this does help us evade a race condition between
     * manually entered order numbers at invoice creation and auto-generated
     * order numbers (see ONCALL-2814 and getShouldAutoGenerateOrderNumsForNotice).
     * This should be kept in mind if the below update is to be removed!
     */
    if (customId) {
      await noticeSnap.ref.update({
        customId
      });
    }

    const [error] = await BillingService.invoiceAdvertiser({
      publisherAmountInCents,
      lineItems,
      inAppTaxPct: taxPercent,
      noticeSnap,
      advertiserSnap,
      newspaperSnap,
      customId,
      customMemo,
      dueDate,
      requireUpfrontPayment,
      invoiceOutsideColumn,
      user
    });
    setIsConfirmingInvoice(false);
    setOpenModal(false);
    if (error) {
      logAndCaptureException(
        ColumnService.PAYMENTS,
        error,
        '[Old Create Invoice]: Failed to create a new invoice',
        { noticeId: noticeSnap.id }
      );
      dispatch(
        ToastActions.toastError({
          headerText: 'Failed to create an invoice',
          bodyText: [
            INVOICE_CREATION_IN_PROGRESS_ERROR,
            NOTICE_HAS_INVOICE_ERROR
          ].includes(error.message)
            ? error.message
            : 'Please try again or contact help@column.us'
        })
      );
    }
    forward();
  };

  const isFeeDistributed = shouldDistributeFee(
    getDistributeFeeSettings(newspaperSnap, rateSnap)
  );

  const affidavitReconciliationSettings = useNoticeAffidavitSettings(
    getFirebaseContext(),
    noticeSnap
  );

  const inAppLineItems = postWithoutFormatting ? emptyLineItems : lineItems;
  const convenienceFee = getConvenienceFeeWithRepFeeFromRateOrNewspaper(
    newspaperSnap,
    rateSnap
  );
  const affidavitDisabled = isAffidavitDisabled(
    noticeSnap.data(),
    newspaperSnap
  );

  const invoicePricing = calculateInvoicePricing(newspaperSnap, {
    inAppLineItems,
    affidavitReconciliationSettings,
    requiresAffidavit: !affidavitDisabled,
    inAppTaxPct: taxPercent,
    convenienceFee,
    mail: mail || [],
    noticePublicationDates: noticeSnap.data().publicationDates,
    rate: rateSnap
  });

  const {
    totalInCents,
    affidavitFeeInCents,
    convenienceFeeInCents,
    subtotalInCents,
    taxInCents
  } = invoicePricing;

  // For historical reasons, this amount is the total of all line items
  // plus tax. In the future we should probably store them separately.
  const publisherAmountInCents = subtotalInCents + taxInCents;

  const processingFeeInCents = affidavitFeeInCents + convenienceFeeInCents;

  const belowMinimum =
    !isNaN(subtotalInCents) &&
    exists(rateSnap) &&
    subtotalInCents < rateSnap.data().minimum;

  const noticeDoesNotHaveInvoice = !noticeSnap.data().invoice;

  const stepComplete =
    !belowMinimum && noticeDoesNotHaveInvoice && !invoiceCreationInitiated;

  useEffect(() => {
    void (async () => {
      if (!exists(newspaperSnap) || !exists(noticeSnap)) {
        return;
      }

      const isWithinBulkPayment_v2 = await shouldBulkInvoiceUser_v2(
        getFirebaseContext(),
        noticeSnap,
        newspaperSnap
      );

      setIsWithinBulkPayments(isWithinBulkPayment_v2);
    })();
  }, [newspaperSnap?.id, noticeSnap?.id]);

  if (
    !exists(newspaperSnap) ||
    !activeOrganization ||
    isWithinBulkPayments === undefined
  ) {
    return <LoadingState context={{ location: 'InvoiceForm' }} />;
  }

  const handleTaxPercentChange = (e: React.ChangeEvent<any>) => {
    const pct = e.target.value;
    setTaxpercent(+parseFloat(pct).toFixed(2));
  };

  const getTermsText = (
    { showDistributeMessage } = { showDistributeMessage: false }
  ) => {
    const { invoiceRecipient } = noticeSnap.data();
    const role = invoiceRecipient ? 'invoice recipient' : 'advertiser';
    const name = invoiceRecipient
      ? getRecipientName(invoiceRecipient)
      : organizationName || advertiserName;

    return `The ${role}, ${name}, will be billed for the advertisement plus taxes and fees. ${
      copy.enoticeName
    } will fill this invoice and pay ${
      newspaperSnap.data().name
    } net 30 from when ${
      affidavitDisabled
        ? 'payment is received.'
        : 'the proof of publication is uploaded.'
    }${
      isFeeDistributed && showDistributeMessage
        ? '</br></br>Column processing fees are for your reference only and will not appear as its own line item in the invoice. You can modify this under Rates Settings.'
        : ''
    }`;
  };

  const currency = getCurrencySymbol(newspaperSnap.data().currency);

  return (
    <>
      <div className="bg-gray-100 ">
        <div
          id="notice-details-page"
          className="max-w-full mx-auto sm:px-6 lg:px-8 lg:pr-0 sm:pr-0"
        >
          <div className="grid grid-cols-8 grid-flow-col gap-8 -ml-8">
            <div className="col-span-8 md:col-span-5 pb-20 overflow-y-scroll h-screen -mr-8">
              <FormStep
                title={'Create Invoice'}
                stepComplete={stepComplete}
                forward={() => {
                  setOpenModal(true);
                }}
                id="invoice-form"
                forwardLabel={forwardLabel}
                back={back}
              >
                <Grid
                  container
                  direction="column"
                  className={classes.formContainer}
                >
                  <Grid
                    container
                    direction="row"
                    spacing={4}
                    justify="space-between"
                  >
                    <Grid item xs={12} md={6}>
                      <InvoiceFormPaperInfoTable
                        organization={newspaperSnap.data()}
                      />
                      <Typography variant="h5"></Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InvoiceFormCustomerTable
                        noticeSnap={noticeSnap}
                        customerName={COLUMN_NAME}
                        setCustomId={setCustomId}
                        customId={customId}
                      />
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    direction="row"
                    className={classes.invoiceHeading}
                  ></Grid>
                </Grid>
                <Grid item xs={12}>
                  <InvoiceFormTable
                    handleChange={handleChange}
                    values={lineItems}
                    currency={currency}
                  />
                </Grid>

                <Grid
                  container
                  direction="row"
                  spacing={4}
                  justify="space-between"
                >
                  <Grid item className={classes.totalDueTable} sm={12} md={6}>
                    {noticeSnap.data().adTemplate && (
                      <InvoiceFormMemo
                        terms={getTermsText({ showDistributeMessage: true })}
                        noticeSnap={noticeSnap}
                        setCustomMemo={setCustomMemo}
                        customMemo={customMemo}
                        newspaper={newspaperSnap.data()}
                        currency={currency}
                      >
                        {' '}
                      </InvoiceFormMemo>
                    )}
                  </Grid>
                  <Grid item className={classes.totalDueTable} sm={12} md={6}>
                    <InvoiceFormTotalDueTable
                      handleTaxPercentChange={handleTaxPercentChange}
                      subtotalInCents={subtotalInCents}
                      belowMinimum={belowMinimum}
                      rateMinimum={dbToUICurrencyString(
                        rateSnap.data().minimum
                      )}
                      taxPercent={taxPercent}
                      totalDueInCents={totalInCents}
                      currency={currency}
                      processingFeeInCents={processingFeeInCents}
                      isFeeDistributed={isFeeDistributed}
                    />
                    <InvoiceFormDueDate
                      newspaper={newspaperSnap}
                      {...{
                        noticeSnap,
                        setDueDate,
                        dueDate,
                        requireUpfrontPayment,
                        setRequireUpfrontPayment,
                        invoiceOutsideColumn,
                        setInvoiceOutsideColumn,
                        ianaTimezone,
                        isWithinBulkPayments
                      }}
                    />
                  </Grid>
                </Grid>
              </FormStep>
            </div>
            <div
              id="sidebar"
              className="hidden md:block md:col-span-3 h-screen z-10"
            >
              <div className="bg-gray-800 h-full">
                <NoticePreview
                  newspaper={newspaperSnap}
                  isPublisher={!!isPublisher}
                  notice={noticeSnap}
                  push={push}
                  showAffinityXSyncPanel={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {openModal && (
        <FreeformCModal
          setOpen={setOpenModal}
          header={
            <span>
              <h6 className="leading-6 text-lg"> Review and Submit </h6>
              <div className="not-italic font-normal text-sm leading-6 flex items-center text-center text-gray-800 mb-4">
                This invoice is due
                {noticeSnap.data().invoiceRecipient
                  ? ' '
                  : ' by the advertiser '}
                on
                <b className="ml-1">
                  {dueDate && ianaTimezone
                    ? moment(dueDate * 1000)
                        .tz(ianaTimezone)
                        .format('MMMM D, YYYY')
                    : ''}
                </b>
              </div>
            </span>
          }
          body={
            <InvoiceConfirm
              getTermsText={getTermsText}
              handleConfirm={handleConfirm}
              isConfirmingInvoice={isConfirmingInvoice}
              disabled={preventSubmit || !stepComplete}
            />
          }
        />
      )}
    </>
  );
}

export default connect(mapStateToProps, { push })(
  withStyles(styles, { withTheme: true })(InvoiceForm)
);
